import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {ApiService} from './api.service';
import {
  EnrichSettings,
  HubspotCompany,
  HubspotImportedCompaniesPage,
  HubspotReadinessResponse,
  HubspotSubUnitPreviewResponse,
  SyncHistory,
} from '../../models/hubspot.model';

type BackendSyncHistory = {
  id?: string | number;
  clientId?: string;
  timestamp?: string;
  type?: SyncHistory['syncType'];
  status?: 'success' | 'failure' | 'failed' | 'partial' | 'in_progress';
  details?: {
    companyId?: string;
    companyName?: string;
    orgNumber?: string;
    error?: string;
    affectedRecords?: number;
    totalRecords?: number;
  };
  companyId?: string;
  companyName?: string;
  message?: string;
  startedAt?: string;
  completedAt?: string;
  syncType?: SyncHistory['syncType'];
  syncedBy?: string;
  changes?: SyncHistory['changes'];
  affectedRecords?: number;
  totalRecords?: number;
  orgNumber?: string;
};

export interface SyncHistoryQueryParams {
  companyName?: string;
  status?: string;
  syncType?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
}

export interface SubUnitPreviewQueryParams {
  limit?: number;
  after?: string;
}

/**
 * Service for interacting with HubSpot data
 * This service handles company data, synchronization, and settings
 */
@Injectable({
  providedIn: 'root'
})
export class HubspotService {
    private apiService = inject(ApiService);


  /**
   * Retrieves all companies from HubSpot
   * @param clientId The client ID to retrieve companies for
   * @returns An Observable with an array of HubSpot companies
   */
  getCompanies(clientId: string): Observable<HubspotCompany[]> {
    return this.apiService.get<HubspotCompany[]>('hubspot/companies', { clientId });
  }

  /**
   * Retrieves one bounded page of companies maintained by Shine Enrich.
   * Provider cursors remain opaque to this client.
   */
  getImportedCompaniesPage(
    clientId: string,
    options: {limit?: number; after?: string | null} = {}
  ): Observable<HubspotImportedCompaniesPage> {
    const params: Record<string, string | number> = {clientId};
    if (options.limit !== undefined) params['limit'] = options.limit;
    if (options.after !== undefined && options.after !== null) params['after'] = options.after;
    return this.apiService.get<HubspotImportedCompaniesPage>('hubspot/companies/imported', params);
  }

  /**
   * Retrieves a specific company from HubSpot by its ID
   * @param id The HubSpot company ID
   * @param clientId The client ID to retrieve the company for
   * @returns An Observable with the HubSpot company
   */
  getCompanyById(id: string, clientId: string): Observable<HubspotCompany> {
    return this.apiService.get<HubspotCompany>(`hubspot/companies/${id}`, { clientId });
  }

  /**
   * Manually triggers a synchronization of a specific company with Brreg data
   * @param id The HubSpot company ID
   * @param clientId The client ID to sync the company for
   * @param force Whether to force the sync regardless of frequency settings
   * @returns An Observable with the sync result
   */
  syncCompany(id: string, clientId: string, force: boolean = false): Observable<any> {
    return this.apiService.post<any>(`hubspot/sync/company/${id}`, {}, { clientId, force });
  }

  /**
   * Manually triggers a synchronization of all companies with Brreg data
   * @param clientId The client ID to sync companies for
   * @param force Whether to force the sync regardless of frequency settings
   * @returns An Observable with the sync result
   */
  syncAllCompanies(clientId: string, force: boolean = false): Observable<any> {
    return this.apiService.post<any>('hubspot/sync/all', {}, { clientId, force });
  }

  /**
   * Retrieves the enrichment settings for a client
   * @param clientId The client ID to retrieve settings for
   * @returns An Observable with the enrichment settings
   */
  getSettings(clientId: string): Observable<EnrichSettings> {
    return this.apiService.get<EnrichSettings>('hubspot/settings', { clientId });
  }

  /**
   * Retrieves backend-derived Enrich readiness for a client.
   * @param clientId The client ID to retrieve readiness for
   * @returns An Observable with the Enrich readiness response
   */
  getReadiness(clientId: string): Observable<HubspotReadinessResponse> {
    return this.apiService.get<HubspotReadinessResponse>('hubspot/readiness', { clientId });
  }

  /**
   * Retrieves a bounded, read-only preview of BRREG sub-unit sync outcomes.
   */
  getSubUnitPreview(clientId: string, params?: SubUnitPreviewQueryParams): Observable<HubspotSubUnitPreviewResponse> {
    const apiParams: Record<string, string | number> = {clientId};

    if (params?.limit !== undefined) {
      apiParams['limit'] = params.limit;
    }

    if (params?.after) {
      apiParams['after'] = params.after;
    }

    return this.apiService.get<HubspotSubUnitPreviewResponse>('hubspot/subunits/preview', apiParams);
  }

  /**
   * Updates the enrichment settings for a client
   * @param settings The updated settings
   * @param clientId The client ID to update settings for
   * @returns An Observable with the updated settings
   */
  updateSettings(settings: EnrichSettings, clientId: string): Observable<EnrichSettings> {
    return this.apiService.put<EnrichSettings>('hubspot/settings', settings, { clientId });
  }

  /**
   * Safely updates the enrichment settings for a client, preventing setupStatus updates after completion
   * @param settings The updated settings
   * @param clientId The client ID to update settings for
   * @returns An Observable with the updated settings
   */
  safeUpdateSettings(settings: EnrichSettings, clientId: string): Observable<EnrichSettings> {
    // First get the current settings to check if setup is complete
    return this.getSettings(clientId).pipe(
      switchMap(currentSettings => {
        // If setup is already complete, preserve the setupStatus
        if (currentSettings.setupStatus?.setupComplete) {
          // Keep the original setupStatus
          settings.setupStatus = currentSettings.setupStatus;
        }
        // Update the settings
        return this.updateSettings(settings, clientId);
      })
    );
  }

  /**
   * Sets up the required HubSpot properties for the Shine Enrich integration
   * @param clientId The client ID to set up properties for
   * @returns An Observable with the setup result
   */
  setup(clientId: string): Observable<any> {
    return this.apiService.post<any>('hubspot/setup', {}, { clientId });
  }

  /**
   * Retrieves the synchronization history for a client
   * @param clientId The client ID to retrieve history for
   * @param params Additional parameters for filtering the history
   * @returns An Observable with an array of sync history records
   */
  getSyncHistory(clientId: string, params?: SyncHistoryQueryParams): Observable<SyncHistory[]> {
    return this.apiService
      .get<BackendSyncHistory[]>('hubspot/sync/history', this.toSyncHistoryApiParams(clientId, params))
      .pipe(map(history => (history ?? []).map(normalizeSyncHistory)));
  }

  /**
   * Retrieves a specific sync history record by its ID
   * @param id The sync history record ID
   * @param clientId The client ID to retrieve the record for
   * @returns An Observable with the sync history record
   */
  getSyncHistoryById(id: string, clientId: string): Observable<SyncHistory> {
    return this.getSyncHistory(clientId).pipe(
      map(history => {
        const match = history.find(item => String(item.id) === String(id));
        if (!match) {
          throw new Error('Sync history not found');
        }
        return match;
      })
    );
  }

  /**
   * Handles a webhook from HubSpot
   * @param data The webhook data
   * @param clientId The client ID the webhook is for
   * @returns An Observable with the webhook handling result
   */
  handleWebhook(data: any, clientId: string): Observable<any> {
    return this.apiService.post<any>('hubspot/webhook', data, { clientId });
  }

  private toSyncHistoryApiParams(clientId: string, params?: SyncHistoryQueryParams): Record<string, string> {
    const apiParams: Record<string, string> = {clientId};

    if (!params) {
      return apiParams;
    }

    if (params.status) {
      apiParams['status'] = this.toApiStatus(params.status);
    }

    const type = params.type || params.syncType;
    if (type) {
      apiParams['type'] = this.toApiType(type);
    }

    if (params.startDate) {
      apiParams['startDate'] = params.startDate;
    }

    if (params.endDate) {
      apiParams['endDate'] = params.endDate;
    }

    return apiParams;
  }

  private toApiStatus(status: string): string {
    return status === 'failed' ? 'failure' : status;
  }

  private toApiType(type: string): string {
    return type;
  }
}

export function normalizeSyncHistory(record: BackendSyncHistory): SyncHistory {
  const details = record.details ?? {};
  const id = record.id === undefined || record.id === null ? '' : String(record.id);
  const startedAt = record.startedAt || record.timestamp || '';
  const message = record.message || details.error;

  return {
    id,
    companyId: record.companyId || details.companyId,
    companyName: record.companyName || details.companyName,
    status: normalizeSyncStatus(record.status),
    message,
    startedAt,
    completedAt: record.completedAt,
    syncType: record.syncType || record.type || 'manual',
    syncedBy: record.syncedBy,
    changes: record.changes,
    affectedRecords: record.affectedRecords ?? details.affectedRecords,
    totalRecords: record.totalRecords ?? details.totalRecords,
    orgNumber: record.orgNumber || details.orgNumber
  };
}

function normalizeSyncStatus(status: BackendSyncHistory['status']): SyncHistory['status'] {
  if (status === 'failure' || status === 'failed') return 'failed';
  if (status === 'partial') return 'partial';
  if (status === 'in_progress') return 'in_progress';
  return 'success';
}
